import * as React from 'react';
import { connect } from 'react-redux';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  Legend,
} from 'recharts';

import { formatAsDollars, percentage } from '../../../../lib/helpers';
import PriceAndRentChart from './PriceAndRentChart';
import Tooltip from '../../../utility/Tooltip';

export interface Props {
  data: any[];
}

const styles = {
  container: {
    marginTop: 40,
  },
  trendText: {
    marginBottom: 20,
  },
};
const darkOrange = '#FFA726';
const darkBlue = '#42a5f5';

const trendText = (d: any) => {
  const lastMonth = d[d.length - 1];
  const bedrooms = d[0].noBedrooms;
  const zip = d[0].zipCode;
  const town = d[0].town;

  const lastMonthPrice = formatAsDollars(lastMonth.priceTrend);
  const trendPriceChange = percentage(
    (lastMonth.priceTrend - d[0].priceTrend) / d[0].priceTrend,
  );
  const priceVerb =
    lastMonth.yoyPriceChange >= 0 ? 'an increase' : 'a decrease';

  const rentLastYear = formatAsDollars(d[0].rentTrend);
  const rentThisYear = formatAsDollars(lastMonth.rentTrend);
  const rentChange = (lastMonth.rentTrend - d[0].rentTrend) / d[0].rentTrend;
  const rentChangePerc = percentage(rentChange);
  const rentVerb = rentChange >= 0 ? 'an increase' : 'a decrease';

  if (
    !lastMonthPrice ||
    !trendPriceChange ||
    !rentLastYear ||
    !rentThisYear ||
    !rentChange
  ) {
    return false;
  }

  return (
    <p style={styles.trendText}>
      The price to buy a {bedrooms}-bedroom unit in {zip} ({town}) was{' '}
      {lastMonthPrice} last month.
      <br />
      That&#39;s <strong>{priceVerb}</strong> of {trendPriceChange} from a year
      ago.
      <br />
      Over the same period, rent went from {rentLastYear} to {rentThisYear};{' '}
      <strong>{rentVerb}</strong> of {rentChangePerc}.
    </p>
  );
};

export const TrendsContent = (props: Props) => (
  <div style={styles.container}>
    {trendText(props.data)}
    <div className="row middle-xs center-xs">
      <h2>Median Price vs. Rent</h2>
      <Tooltip text="Shows how much people are paying for houses (blue) and how much they&339;re renting them for (orange)." />
    </div>
    <PriceAndRentChart data={props.data} />

    <br />

    <div className="row middle-xs center-xs">
      <h2>Price and Rent Trends</h2>
      <Tooltip text="Shows how much the values from the above chart are changing over time." />
    </div>
    <ResponsiveContainer width="100%" height={350}>
      <ComposedChart
        data={props.data}
        margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
      >
        <XAxis dataKey="month" />
        <YAxis tickFormatter={percentage} />
        <ReTooltip
          formatter={(value: any) => {
            return `${(value * 100).toFixed(1)}%`;
          }}
        />
        <Legend />
        <Line
          type="monotone"
          dataKey="yoyPriceChange"
          name="Purchase Price Change"
          stroke={darkBlue}
          connectNulls
        />
        <Line
          type="monotone"
          dataKey="yoyRentChange"
          name="Rent Change"
          stroke={darkOrange}
          connectNulls
        />
        <Line
          type="monotone"
          dataKey="origin"
          stroke="#999999"
          dot={false}
          activeDot={false}
          legendType="none"
        />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

export default connect()(TrendsContent);
